/**
 * @module booking-form
 * Manages the interactive booking form, including date pickers and guest selection panels.
 * This is designed to be reusable for both header and shortcode versions of the form.
 */

// --- Constants ---
const ATTR_BOOKING_FORM = 'data-booking-form';
const ATTR_PANEL_TRIGGER = 'data-booking-panel-trigger';
const ATTR_PANEL = 'data-booking-panel';
const ATTR_INPUT = 'data-booking-input';

// --- Module-level State ---
let activePicker = null;
let activePanel = null;
let activeTrigger = null;
let activeForm = null; // Keep track of the form associated with the active panel

/**
 * Hides any currently open panel.
 */
function hideActivePanel() {
    if (activeForm) {
        updateButtonLabels(activeForm);
        activeForm = null;
    }
    if (activePanel) {
        activePanel.setAttribute('aria-hidden', 'true');
        activePanel = null;
    }
    if (activeTrigger) {
        activeTrigger.setAttribute('aria-expanded', 'false');
        activeTrigger = null;
    }
    if (activePicker) {
        activePicker.hide();
    }
}

/**
 * Updates the label of a button based on form inputs.
 * @param {HTMLElement} form - The form element.
 */
function updateButtonLabels(form) {
    // Update Dates Button
    const arrivalInput = form.querySelector(`[${ATTR_INPUT}="arrival"]`);
    const departureInput = form.querySelector(`[${ATTR_INPUT}="departure"]`);
    const dateButtonLabel = form.querySelector(`[${ATTR_PANEL_TRIGGER}="dates"] .booking-form__button-label`);

    if (arrivalInput && departureInput && dateButtonLabel) {
        if (arrivalInput.value && departureInput.value) {
            const arrivalDate = new Date(`${arrivalInput.value}T00:00:00`);
            const departureDate = new Date(departureInput.value + 'T00:00:00');
            
            // Custom date formatting function for '12 Nov 25'' format
            const formatDate = (date) => {
                const day = date.getDate();
                const month = date.toLocaleString('en-AU', { month: 'short' });
                const year = date.getFullYear().toString().slice(-2);
                return `${day} ${month} '${year}`;
            };
            dateButtonLabel.textContent = `${formatDate(arrivalDate)} to ${formatDate(departureDate)}`;
        } else {
            dateButtonLabel.textContent = dateButtonLabel.dataset.defaultLabel;
        }
    }

    // Update Guests Button
    const adultsInput = form.querySelector('input[name="adults"]');
    const childrenInput = form.querySelector('input[name="children"]');
    const guestsButtonLabel = form.querySelector(`[${ATTR_PANEL_TRIGGER}="guests"] .booking-form__button-label`);

    if (adultsInput && childrenInput && guestsButtonLabel) {
        const adults = parseInt(adultsInput.value, 10) || 0;
        const children = parseInt(childrenInput.value, 10) || 0;
        const totalGuests = adults + children;

        // On initial load, only show guest count if it's in the URL. Otherwise, show the default label.
        // On subsequent updates, always show the current value from the inputs.
        const isInitialUpdate = !form.dataset.initialized;
        const urlParams = new URLSearchParams(window.location.search);
        const hasGuestParamsInURL = urlParams.has('adults') || urlParams.has('children');

        if (totalGuests > 0 && (!isInitialUpdate || hasGuestParamsInURL)) {
            guestsButtonLabel.textContent = `${totalGuests} Guest${totalGuests > 1 ? 's' : ''}`;
        } else {
            guestsButtonLabel.textContent = guestsButtonLabel.dataset.defaultLabel;
        }
    }
}

/**
 * Initializes AJAX functionality for a given booking form.
 * This handles form submission, updates the results container, and manages browser history.
 * @param {HTMLElement} form - The booking form element.
 */
function initializeAjaxForm(form) {
    // This form doesn't submit to a results page, so it should not be handled by this AJAX logic.
    // The form's standard GET submission will handle the page redirect.
    if (form.dataset.ajaxEnabled !== 'true') {
        return;
    }

    let abortController = null;

    /**
     * Performs an AJAX search and updates the property list.
     * @param {string} searchUrl - The URL to fetch search results from.
     * @returns {void}
     */
    const performSearch = (searchUrl) => {
        // Abort any previous fetch request to prevent race conditions.
        if (abortController) {
            abortController.abort();
        }
        abortController = new AbortController();

        const resultsContainer = document.getElementById('rms-property-list-container');
        const loadingIndicator = document.getElementById('rms-loading-indicator');

        if (!resultsContainer) {
            console.error('RMS Search: Results container or loading indicator not found. Falling back to page reload.');
            window.location.href = searchUrl;
            return;
        }

        loadingIndicator.style.display = 'block';
        resultsContainer.style.opacity = '0.5';
        resultsContainer.setAttribute('aria-busy', 'true');

        // Append required AJAX parameters to the URL.
        const url = new URL(searchUrl);
        url.searchParams.append('action', 'rms_filter_properties');
        url.searchParams.append('nonce', window.rms_ajax_object.nonce);

        fetch(`${window.rms_ajax_object.ajax_url}?${url.searchParams.toString()}`, {
            method: 'GET',
            signal: abortController.signal
        })
        .then(response => response.ok ? response.text() : Promise.reject('Network response was not ok.'))
        .then(html => {
            resultsContainer.innerHTML = html;
        })
        .catch(error => {
            if (error.name !== 'AbortError') {
                console.error('There was a problem with your fetch operation:', error);
                resultsContainer.innerHTML = '<p>Sorry, an error occurred while searching. Please try again.</p>';
            }
        })
        .finally(() => {
            if (!abortController.signal.aborted) {
                loadingIndicator.style.display = 'none';
                resultsContainer.style.opacity = '1';
                resultsContainer.setAttribute('aria-busy', 'false');
            }
        });
    };

    // --- Event Listener for Form Submission ---
    form.addEventListener('submit', function(e) {
        e.preventDefault(); // Prevent the default page reload

        // Construct the search URL from the form's action and inputs
        const formData = new FormData(form);
        const params = new URLSearchParams(formData);
        const searchUrl = `${form.action}?${params.toString()}`;

        // Update the browser's URL and history state
        history.pushState({ path: searchUrl }, '', searchUrl);

        // Perform the AJAX search
        performSearch(searchUrl);

        // Close any open booking form panels
        hideActivePanel();
    });

    // --- Browser History Management (Back/Forward buttons) ---
    // This listener is only added once per page load.
    if (!window.rmsPopstateListener) {
        // Set the initial history state for the current page.
        history.replaceState({ path: window.location.href }, '', window.location.href);

        const handlePopState = (event) => {
            // Ignore popstate events without a state object, which can happen on initial load.
            if (!event.state || !event.state.path) {
                return;
            }

            // Determine if this is an in-page history change (e.g., search filter update)
            // or a navigation to a completely different page.
            const currentPathname = window.location.pathname;
            const historyPathname = new URL(event.state.path, window.location.origin).pathname;
            const resultsContainer = document.getElementById('rms-property-list-container');

            // If pathnames match and we're on a page with the results container, handle it via AJAX.
            // Otherwise, let Barba.js or the browser handle the page transition.
            if (currentPathname === historyPathname && resultsContainer) {
                // Prevent Barba.js's popstate listener from firing and causing a full page transition.
                event.stopImmediatePropagation();

                performSearch(event.state.path);

                // To fully sync the UI, you would parse event.state.path and update the
                // booking form input values to match the URL.
            }
        };

        // Use the "capture" phase to ensure our listener runs before Barba's.
        window.addEventListener('popstate', handlePopState, true);
        window.rmsPopstateListener = true;
    }
}


/**
 * Initializes a single booking form instance.
 * @param {HTMLElement} form - The form element to initialize.
 */
function initializeForm(form) {
    // If the form is already initialized, do nothing. This prevents re-attaching listeners.
    if (form.dataset.initialized) {
        return;
    }

    // Initialize AJAX functionality if the form is marked for it.
    initializeAjaxForm(form);

    const dateTriggerButton = form.querySelector(`[${ATTR_PANEL_TRIGGER}="dates"] .booking-form__button-toggle`);
    const guestsTriggerButton = form.querySelector(`[${ATTR_PANEL_TRIGGER}="guests"] .booking-form__button-toggle`);
    const guestsPanel = form.querySelector(`[${ATTR_PANEL}="guests"]`);
    const datePanel = form.querySelector(`[${ATTR_PANEL}="dates"]`);

    // --- Initialize Litepicker for the date range ---
    if (dateTriggerButton && typeof Litepicker !== 'undefined') {
        const arrivalInput = form.querySelector(`[${ATTR_INPUT}="arrival"]`);
        const departureInput = form.querySelector(`[${ATTR_INPUT}="departure"]`);
        const displayInput = form.querySelector('.booking-form__daterange-display');

        const picker = new Litepicker({ // This picker is now only for display inside the panel
            element: displayInput, // Attached to a dummy input
            parentEl: datePanel.querySelector('.booking-form__panel-content'),
            inlineMode: true,
            singleMode: false,
            autoApply: true,
            minDate: new Date(new Date().setHours(0, 0, 0, 0)),
            numberOfMonths: 1,
            numberOfColumns: 1,
            format: 'DD MMM YYYY',
            setup: (pickerInstance) => {
                pickerInstance.on('selected', (date1, date2) => {
                    if (date1 && date2) {
                        arrivalInput.value = date1.format('YYYY-MM-DD');
                        departureInput.value = date2.format('YYYY-MM-DD');
                        hideActivePanel();
                        // Use a small timeout to allow the picker to show the selection
                        // before the panel closes.
                        setTimeout(hideActivePanel, 100);
                    }
                });
            },
        });

        if (arrivalInput.value && departureInput.value) {
            picker.setDateRange(arrivalInput.value, departureInput.value);
        }

        dateTriggerButton.addEventListener('click', (e) => {
            e.preventDefault();
            if (activePanel === datePanel) {
                hideActivePanel();
            } else {
                hideActivePanel();
                datePanel.setAttribute('aria-hidden', 'false');
                dateTriggerButton.setAttribute('aria-expanded', 'true');
                activePanel = datePanel;
                activeTrigger = dateTriggerButton;
                activePicker = picker;
                activeForm = form;
            }
        });
    }

    // --- Initialize Guest Picker Panel ---
    if (guestsTriggerButton && guestsPanel) {
        guestsTriggerButton.addEventListener('click', (e) => {
            e.preventDefault();
            if (activePanel === guestsPanel) {
                hideActivePanel();
            } else {
                hideActivePanel();
                guestsPanel.setAttribute('aria-hidden', 'false');
                guestsTriggerButton.setAttribute('aria-expanded', 'true');
                activePanel = guestsPanel;
                activeTrigger = guestsTriggerButton;
                activeForm = form;
            }
        });
        // Removed live 'change' listeners as per request.
    }

    // --- Initial Label Update ---
    updateButtonLabels(form);

    // Mark the form as initialized to prevent re-running this logic.
    form.dataset.initialized = 'true';

    // --- Global Click Listener to Close Panels ---
    // This listener is only added once.
    if (!window.bookingFormGlobalListener) {
        document.addEventListener('click', (e) => {
            if (
                activePanel && // A panel is open
                activeForm && // A form is active...
                !activeForm.contains(e.target) && // ...and the click was outside the form...
                !e.target.closest('.litepicker') && // ...and not inside a Litepicker instance...
                !e.target.closest(`[${ATTR_PANEL_TRIGGER}]`) && // ...and not on any panel trigger button.
                !e.target.closest('a, button') && // Click is not on a link or button
                !e.target.classList.contains('day-item') // Click is not on a calendar day
            ) {
                // Clicks on other interactive elements are ignored by this logic.
                hideActivePanel();
            }
        });
        window.bookingFormGlobalListener = true;
    }
}

/**
 * Finds and initializes all booking forms on the page.
 */
export function initializeBookingForms() {
    const forms = document.querySelectorAll(`[${ATTR_BOOKING_FORM}]`);
    forms.forEach(initializeForm);
}